export interface MarketingPageContext {
  surface: "marketing";
  page_origin?: string;
  page_path: string;
  page_title?: string;
  campaign_source?: string;
  campaign_medium?: string;
  campaign_name?: string;
}

export interface ProductPageContext {
  surface: "product";
  route: string;
}

export interface AnalyticsEventProperties {
  marketing_page_view: MarketingPageContext;
  waitlist_cta_clicked: MarketingPageContext;
  product_signup_cta_clicked: MarketingPageContext;
  waitlist_submission_succeeded: Pick<MarketingPageContext, "surface" | "page_path">;
  product_page_view: ProductPageContext;
  customer_session_started: {
    surface: "product";
    role: string;
    account_mode: "customer";
  };
  saved_search_created: { surface: "product" };
  property_analysis_requested: { surface: "product"; analysis_kind: string };
  property_analysis_completed: {
    surface: "product";
    analysis_kind: string;
    outcome: "completed";
  };
  checkout_started: { surface: "product"; pack_key: string };
}

export type AnalyticsEventName = keyof AnalyticsEventProperties;

export type AnalyticsEvent = {
  [Name in AnalyticsEventName]: {
    name: Name;
    properties: AnalyticsEventProperties[Name];
  };
}[AnalyticsEventName];

const EVENT_KEYS: { [Name in AnalyticsEventName]: ReadonlySet<string> } = {
  marketing_page_view: new Set([
    "surface",
    "page_origin",
    "page_path",
    "page_title",
    "campaign_source",
    "campaign_medium",
    "campaign_name",
  ]),
  waitlist_cta_clicked: new Set([
    "surface",
    "page_origin",
    "page_path",
    "page_title",
    "campaign_source",
    "campaign_medium",
    "campaign_name",
  ]),
  product_signup_cta_clicked: new Set([
    "surface",
    "page_origin",
    "page_path",
    "page_title",
    "campaign_source",
    "campaign_medium",
    "campaign_name",
  ]),
  waitlist_submission_succeeded: new Set(["surface", "page_path"]),
  product_page_view: new Set(["surface", "route"]),
  customer_session_started: new Set(["surface", "role", "account_mode"]),
  saved_search_created: new Set(["surface"]),
  property_analysis_requested: new Set(["surface", "analysis_kind"]),
  property_analysis_completed: new Set(["surface", "analysis_kind", "outcome"]),
  checkout_started: new Set(["surface", "pack_key"]),
};

const CAMPAIGN_VALUES = new Set([
  "youtube",
  "instagram",
  "facebook",
  "linkedin",
  "newsletter",
  "community",
  "organic_social",
  "social",
  "email",
  "referral",
  "video",
  "waitlist_launch",
  "north_florida_launch",
  "product_launch",
]);

function isSafeString(value: unknown, maxLength = 120): value is string {
  return (
    typeof value === "string" &&
    value.length > 0 &&
    value.length <= maxLength &&
    !value.includes("@") &&
    !/[?#]/.test(value)
  );
}

function isAllowedProperty(key: string, value: unknown): boolean {
  if (key === "surface") return value === "marketing" || value === "product";
  if (key === "page_origin")
    return value === "https://veracio.app" || value === "https://app.veracio.app";
  if (key === "page_path" || key === "route")
    return isSafeString(value, 160) && value.startsWith("/");
  if (key === "page_title") return isSafeString(value, 120);
  if (key.startsWith("campaign_")) return typeof value === "string" && CAMPAIGN_VALUES.has(value);
  if (key === "account_mode") return value === "customer";
  if (key === "outcome") return value === "completed";
  return isSafeString(value, 64) && /^[a-z0-9][a-z0-9_-]*$/i.test(value);
}

export function sanitizeEventProperties<Name extends AnalyticsEventName>(
  name: Name,
  properties: AnalyticsEventProperties[Name],
): Partial<AnalyticsEventProperties[Name]> {
  const result: Record<string, string> = {};
  for (const [key, value] of Object.entries(properties)) {
    if (EVENT_KEYS[name].has(key) && isAllowedProperty(key, value)) result[key] = value as string;
  }
  return result as Partial<AnalyticsEventProperties[Name]>;
}
